/** Fit every corner of an oriented model's AABB inside the actual camera frustum.
 * Public model-viewer APIs supply world-space bounds and the effective vertical
 * FOV (which model-viewer adjusts for aspect ratio). No asset-specific scale.
 */
export function fitAvatarBounds(dimensions, center, { width, height, fieldOfView, theta = 0, phi = Math.PI / 2, padding = 1.18 } = {}) {
  const values = [dimensions?.x, dimensions?.y, dimensions?.z, center?.x, center?.y, center?.z, width, height, fieldOfView, theta, phi, padding];
  if (!values.every(Number.isFinite) || width <= 0 || height <= 0 || fieldOfView <= 0 || fieldOfView >= 179 || padding < 1) return null;
  if ([dimensions.x, dimensions.y, dimensions.z].some((n) => n < 0) || Math.max(dimensions.x, dimensions.y, dimensions.z) === 0) return null;

  const verticalTangent = Math.tan(fieldOfView * Math.PI / 360);
  const horizontalTangent = verticalTangent * width / height;
  const right = [Math.cos(theta), 0, -Math.sin(theta)];
  const up = [-Math.cos(phi) * Math.sin(theta), Math.sin(phi), -Math.cos(phi) * Math.cos(theta)];
  const towardCamera = [Math.sin(phi) * Math.sin(theta), Math.cos(phi), Math.sin(phi) * Math.cos(theta)];
  const dot = (a, b) => a.reduce((sum, n, i) => sum + n * b[i], 0);
  let radius = 0;
  for (const x of [-1, 1]) for (const y of [-1, 1]) for (const z of [-1, 1]) {
    const corner = [x * dimensions.x / 2, y * dimensions.y / 2, z * dimensions.z / 2];
    const depth = dot(corner, towardCamera);
    radius = Math.max(radius,
      depth + padding * Math.abs(dot(corner, right)) / horizontalTangent,
      depth + padding * Math.abs(dot(corner, up)) / verticalTangent);
  }
  if (!Number.isFinite(radius) || radius <= 0) return null;
  return {
    radius,
    orbit: `${theta}rad ${phi}rad ${radius}m`,
    target: `${center.x}m ${center.y}m ${center.z}m`,
  };
}

/** Refit after load (including fallback assets), orientation changes and resize.
 * Cleanup invalidates in-flight work; observers never run a per-frame loop.
 */
export function observeAvatarFraming(model) {
  let disposed = false;
  let frame = 0;
  let revision = 0;
  const fit = async () => {
    const current = ++revision;
    try {
      await model.updateComplete;
      if (disposed || current !== revision || !model.loaded) return;
      // Recompute oriented bounds, rather than using the source GLB's Z-up box.
      await model.updateFraming();
      if (disposed || current !== revision) return;
      model.jumpCameraToGoal();
      await model.updateComplete;
      await Promise.resolve();
      if (disposed || current !== revision || !model.loaded) return;
      const rect = model.getBoundingClientRect();
      const { theta, phi } = model.getCameraOrbit();
      const fitted = fitAvatarBounds(model.getDimensions(), model.getBoundingBoxCenter(), {
        width: rect.width, height: rect.height, fieldOfView: model.getFieldOfView(), theta, phi,
      });
      if (!fitted) return;
      // Explicit limits keep model-viewer's default radius cap from undoing fit.
      model.maxCameraOrbit = `auto auto ${fitted.radius * 2}m`;
      model.minCameraOrbit = 'auto auto 0m';
      model.cameraTarget = fitted.target;
      model.cameraOrbit = fitted.orbit;
      await model.updateComplete;
      if (!disposed && current === revision) model.jumpCameraToGoal();
    } catch {
      // Keep model-viewer's conservative automatic frame if bounds are not ready.
      // Asset load failures are handled by AvatarModel's existing fallback path.
    }
  };
  const schedule = () => {
    ++revision;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => { void fit(); });
  };
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
  model.addEventListener('load', schedule);
  observer?.observe(model);
  window.addEventListener('resize', schedule);
  schedule();
  return () => {
    disposed = true;
    ++revision;
    cancelAnimationFrame(frame);
    observer?.disconnect();
    model.removeEventListener('load', schedule);
    window.removeEventListener('resize', schedule);
  };
}
